import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import LayoutHeader from "../../components/LayoutHeader";
import LayoutBackground from "../../components/LayoutBackground";
import { ReactComponent as PerkataanIcon } from "../../assets/icons/dashboard/layout/perkataanIcon.svg";
import { ReactComponent as PasswordIcon } from "../../assets/icons/dashboard/layout/passwordIcon.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/dashboard/layout/userIcon.svg";
import { ReactComponent as RoleIcon } from "../../assets/icons/dashboard/layout/roleIcon.svg";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";

const MenuItems = [
  { to: "/", icon: <PerkataanIcon />, label: "Perkataan" },
  { to: "/user-list", icon: <UserIcon />, label: "Users" },
  { to: "/role-list", icon: <RoleIcon />, label: "Roles" },
  { to: "/change-password", icon: <PasswordIcon />, label: "Change Password" },
];

export default function Layout() {
  const { logout, user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openUserMenu = Boolean(anchorEl);

  React.useEffect(() => {
    const handleAutoLogout = () => {
      logout();
      navigate("/login", { replace: true });
    };

    window.addEventListener("auth:logout", handleAutoLogout);
    return () => window.removeEventListener("auth:logout", handleAutoLogout);
  }, [logout, navigate]);

  const perkataanView = user?.permissions?.perkataan?.view;
  const userView = user?.permissions?.user?.view;
  const roleView = user?.permissions?.role?.view;

  const filteredMenuItems = MenuItems.filter((item) => {
    if (item.to === "/" && perkataanView) return true;
    if (item.to === "/user-list" && userView) return true;
    if (item.to === "/role-list" && roleView) return true;
    if (item.to === "/change-password") return true;
    return false;
  });

  const isActive = (to) => {
    if (to === "/") {
      return (
        location.pathname === "/" ||
        location.pathname.startsWith("/add-perkataan") ||
        location.pathname.startsWith("/edit-perkataan")
      );
    }
    return location.pathname.startsWith(to);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    logout();
    setLogoutDialogOpen(false);
    navigate("/login", { replace: true });
  };

  // Generate initials from name or email
  const getInitials = () => {
    if (user?.name) {
      const names = user.name.split(" ");
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user?.email?.substring(0, 2).toUpperCase() || "U";
  };

  // Generate consistent color from string
  const getAvatarColor = (str) => {
    const colors = [
      "#1976d2",
      "#388e3c",
      "#d32f2f",
      "#7b1fa2",
      "#f57c00",
      "#0097a7",
      "#c2185b",
      "#5d4037",
    ];
    let hash = 0;
    for (let i = 0; i < (str || "").length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <LayoutBackground>
      {/* Header */}
      <Box sx={{ position: "relative" }}>
        <LayoutHeader title="GLOSARI BUDAYA QURANI" />

        {/* User Button (top right with gradient background) */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 24,
            transform: "translateY(-50%)",
          }}
        >
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: openUserMenu
                ? "linear-gradient(135deg, #1F4B3F 0%, #2d6b5a 100%)"
                : "linear-gradient(135deg, #1F4B3F 0%, #2d6b5a 100%)",
              boxShadow: "0 4px 12px rgba(31, 75, 63, 0.3)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(31, 75, 63, 0.4)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                fontSize: 15,
                fontWeight: 600,
                bgcolor: getAvatarColor(user?.email || user?.name),
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            >
              {getInitials()}
            </Avatar>

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: "#fff",
                }}
              >
                {user?.name || user?.email?.split("@")[0] || "User"}
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.85)",
                  textTransform: "capitalize",
                }}
              >
                {user?.role || "Role"}
              </Typography>
            </Box>

            <KeyboardArrowDownIcon
              sx={{
                fontSize: 20,
                color: "#fff",
                transform: openUserMenu ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={openUserMenu}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            width: 280,
            borderRadius: "12px",
            mt: 1.5,
            overflow: "visible",
            filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.1))",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                fontSize: 18,
                fontWeight: 600,
                bgcolor: getAvatarColor(user?.email || user?.name),
              }}
            >
              {getInitials()}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.name || user?.email?.split("@")[0] || "User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>

          {/* Role Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              backgroundColor: "rgba(31, 75, 63, 0.08)",
              border: "1px solid",
              borderColor: "rgba(31, 75, 63, 0.2)",
            }}
          >
            <PersonIcon sx={{ fontSize: 14, color: "#1F4B3F" }} />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#1F4B3F",
                textTransform: "capitalize",
              }}
            >
              {user?.role || "Role"}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Menu Items */}
        <Box sx={{ py: 1 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 2.5,
              py: 1.25,
              fontSize: 14,
              fontWeight: 500,
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.50",
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ fontSize: 20, color: "error.main" }} />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Box>
      </Menu>

      {/* Layout Body */}
      <Box display="flex" className="gap-10 pl-10" sx={{ height: "82vh" }}>
        {/* Sidebar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            py: 2,
          }}
        >
          {filteredMenuItems.map((item, idx) => (
            <Box
              key={idx}
              onClick={() => navigate(item.to)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: 150,
                height: 44,
                borderRadius: "10px",
                pl: 2,
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: isActive(item.to)
                  ? theme.palette.buttonHover.main
                  : theme.palette.white.main,
                color: isActive(item.to)
                  ? theme.palette.white.main
                  : theme.palette.black.main,
                boxShadow: isActive(item.to)
                  ? "0 2px 6px rgba(0,0,0,0.15)"
                  : "0 1px 3px rgba(0,0,0,0.05)",
                transition: "all .2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.buttonHover.main,
                  color: theme.palette.white.main,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              {item.icon}
              <span className="text-[13px] font-inter">{item.label}</span>
            </Box>
          ))}
        </Box>

        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, pr: 2 }}>
          <Stack
            sx={{
              height: "100%",
              backgroundColor: theme.palette.white.main,
              borderRadius: "20px",
              border: `1px solid ${theme.palette.black.main}`,
              overflow: "hidden",
            }}
          >
            <Outlet />
          </Stack>
        </Box>
      </Box>

      <ConfirmDialog
        open={logoutDialogOpen}
        title="Log Out Confirmation"
        content="Are you sure you want to log out?"
        confirmText="Log Out"
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={confirmLogout}
      />
    </LayoutBackground>
  );
}
